/**
 * AdminAttendancePage — Org Attendance Snapshot + Daily drill-down.
 *
 * Layout: tabs (Snapshot / Daily / By Employee). Each tab is lazy-mounted
 * via <DataTabs>; inactive tabs contribute zero render cost.
 *
 * Data is fed by:
 *   GET /api/admin/attendance/snapshot
 *   GET /api/admin/attendance/daily
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Clock, LogIn, LogOut, AlertTriangle, BarChart3,
  CalendarDays, Users, TrendingUp, Grid3x3,
} from 'lucide-react';
import DataTabs from '../../components/data/DataTabs';
import DataTable from '../../components/data/DataTable';
import ThresholdFilter from '../../components/data/ThresholdFilter';
import useInfiniteList from '../../components/data/useInfiniteList';
import AttendanceHeatmap from '../../components/data/AttendanceHeatmap';
import { formatDateToLocal, getTodayLocal } from '../../lib/dateUtils';

const API = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({
  Authorization: `Bearer ${localStorage.getItem('admin_token') || localStorage.getItem('adminToken')}`,
});

// ─── KPI tile ──────────────────────────────────────────────────────────────
const Tile = ({ icon: Icon, label, value, sub, accent = 'pink', testid }) => {
  const accents = {
    pink: 'bg-pink-50 text-pink-700 border-pink-100',
    blue: 'bg-blue-50 text-blue-700 border-blue-100',
    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-100',
    amber: 'bg-amber-50 text-amber-700 border-amber-100',
    red: 'bg-red-50 text-red-700 border-red-100',
    neutral: 'bg-neutral-50 text-neutral-700 border-neutral-200',
  };
  return (
    <div className="bg-white border border-neutral-200 rounded-lg p-4" data-testid={testid}>
      <div className="flex items-center gap-2 text-xs text-neutral-500 uppercase tracking-wider">
        <span className={`inline-flex items-center justify-center w-6 h-6 rounded-md border ${accents[accent]}`}>
          <Icon className="w-3.5 h-3.5" />
        </span>
        {label}
      </div>
      <div className="mt-2 text-2xl font-semibold text-[#1a1a2e] tabular-nums">{value ?? '—'}</div>
      {sub && <div className="text-[11px] text-neutral-500 mt-0.5">{sub}</div>}
    </div>
  );
};

// ─── Snapshot Tab ──────────────────────────────────────────────────────────
export const SnapshotTab = ({ fromDate, toDate, employeeId, department, onPickEmployee }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await axios.get(`${API}/api/admin/attendance/snapshot`, {
        headers: auth(),
        params: {
          from_date: fromDate,
          to_date: toDate,
          department: department || undefined,
        },
      });
      setData(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate, department]);

  useEffect(() => { load(); }, [load]);

  const tiles = data?.tiles || {};
  // If an employee is selected via the outer filter bar, narrow the rows
  // client-side (the snapshot endpoint is org-aggregating; per-emp narrow
  // happens here so the tiles still show "the org for this dept" totals).
  const rows = useMemo(() => {
    const all = data?.rows || [];
    if (!employeeId) return all;
    return all.filter((r) => r.employee_id === employeeId);
  }, [data, employeeId]);

  const cols = [
    { key: 'name', header: 'Employee', sortable: true,
      render: (r) => (
        <div>
          <div className="font-medium text-[#1a1a2e]">{r.name}</div>
          <div className="text-[11px] text-neutral-500 font-mono">{r.empcode}</div>
        </div>
      ) },
    { key: 'department', header: 'Dept', sortable: true, width: 110 },
    { key: 'days_present', header: 'Present', sortable: true, align: 'right', width: 80 },
    { key: 'days_absent', header: 'Absent', sortable: true, align: 'right', width: 80,
      render: (r) => <span className={r.days_absent > 0 ? 'text-red-600 font-medium' : ''}>{r.days_absent}</span> },
    { key: 'days_late', header: 'Late', sortable: true, align: 'right', width: 70,
      render: (r) => <span className={r.days_late >= 3 ? 'text-amber-700 font-semibold' : ''}>{r.days_late}</span> },
    { key: 'days_early_out', header: 'Early Out', sortable: true, align: 'right', width: 90 },
    { key: 'work_hhmm', header: 'Work', align: 'right', width: 90 },
    { key: 'overtime_hhmm', header: 'OT', align: 'right', width: 80,
      render: (r) => <span className={r.overtime_minutes > 0 ? 'text-emerald-700 font-medium' : ''}>{r.overtime_hhmm}</span> },
    { key: 'avg_check_in', header: 'Avg In', align: 'right', width: 80 },
    { key: 'avg_check_out', header: 'Avg Out', align: 'right', width: 80 },
  ];

  return (
    <div className="space-y-5">
      {/* KPI tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        <Tile icon={Users} label="Employees" value={tiles.employees_with_data} accent="neutral" testid="tile-emp" />
        <Tile icon={CalendarDays} label="Present-Days" value={tiles.total_present_days} accent="emerald" testid="tile-present" />
        <Tile icon={AlertTriangle} label="Absent-Days" value={tiles.total_absent_days} accent="red" testid="tile-absent" />
        <Tile icon={Clock} label="Late Days" value={tiles.total_late_days} accent="amber" testid="tile-late" />
        <Tile icon={LogOut} label="Early Out" value={tiles.total_early_out_days} accent="amber" testid="tile-early" />
        <Tile icon={TrendingUp} label="Total OT" value={tiles.total_overtime_hhmm || '0h 00m'} sub={`${tiles.total_overtime_minutes ?? 0} min`} accent="blue" testid="tile-ot" />
        <Tile icon={LogIn} label="Avg In / Out" value={`${tiles.avg_check_in || '—'} / ${tiles.avg_check_out || '—'}`} accent="pink" testid="tile-avg" />
      </div>

      {/* Per-employee table */}
      <DataTable
        columns={cols}
        items={rows}
        isLoading={loading}
        error={error}
        onRowClick={(r) => onPickEmployee?.(r)}
        getRowKey={(r) => r.employee_id}
        searchable
        csvExport={{ filename: 'attendance_snapshot' }}
        emptyState="No machine data in this range — sync data or adjust dates."
        testid="attendance-snapshot-table"
      />
      {data?.cutoff_applied && (
        <p className="text-[11px] text-amber-700 inline-flex items-center gap-1">
          <AlertTriangle className="w-3 h-3" />
          Data cutoff active — only data from {data.from} onwards is counted.
        </p>
      )}
    </div>
  );
};

// ─── Daily Tab ─────────────────────────────────────────────────────────────
export const DailyTab = ({
  fromDate, toDate, employeeId, department,
  // When parent owns these filters (Reports hub), it passes them in.
  // When this Tab is used standalone, defaults are used.
  lateGt: lateGtProp,
  otGt: otGtProp,
  workLt: workLtProp,
  status: statusProp,
  presetLateGt,
  hideInternalFilters = false,
}) => {
  const [lateGtLocal, setLateGtLocal] = useState(presetLateGt ?? null);
  const [otGtLocal, setOtGtLocal] = useState(null);
  const [workLtLocal, setWorkLtLocal] = useState(null);
  const [statusFilterLocal, setStatusFilterLocal] = useState('');

  // Controlled-when-provided pattern: prop wins, fallback to local.
  const lateGt = lateGtProp !== undefined ? lateGtProp : lateGtLocal;
  const otGt = otGtProp !== undefined ? otGtProp : otGtLocal;
  const workLt = workLtProp !== undefined ? workLtProp : workLtLocal;
  const statusFilter = statusProp !== undefined ? statusProp : statusFilterLocal;

  const params = {
    from_date: fromDate,
    to_date: toDate,
    employee_id: employeeId || undefined,
    department: department || undefined,
    late_gt: lateGt ?? undefined,
    overtime_gt: otGt ?? undefined,
    work_lt: workLt ?? undefined,
    status: statusFilter || undefined,
  };

  const { items, isLoading, isLoadingMore, hasMore, loadMore, error, refresh } = useInfiniteList(
    '/api/admin/attendance/daily',
    params,
    { pageSize: 100, authHeaders: auth },
  );

  const cols = [
    { key: 'date', header: 'Date', sortable: false, width: 110,
      render: (r) => <span className="font-mono text-xs">{r.date}</span> },
    { key: 'name', header: 'Employee', sortable: false,
      render: (r) => (
        <div>
          <div className="font-medium">{r.name}</div>
          <div className="text-[10px] text-neutral-500 font-mono">{r.empcode}</div>
        </div>
      ) },
    { key: 'in_time', header: 'In', align: 'right', width: 70,
      render: (r) => <span className="font-mono text-xs">{r.in_time || '—'}</span> },
    { key: 'out_time', header: 'Out', align: 'right', width: 70,
      render: (r) => <span className="font-mono text-xs">{r.out_time || '—'}</span> },
    { key: 'work_minutes', header: 'Work', align: 'right', width: 90,
      render: (r) => fmtMin(r.work_minutes) },
    { key: 'overtime_minutes', header: 'OT', align: 'right', width: 80,
      render: (r) => <span className={r.overtime_minutes > 0 ? 'text-emerald-700' : 'text-neutral-400'}>{fmtMin(r.overtime_minutes)}</span> },
    { key: 'late_minutes', header: 'Late', align: 'right', width: 80,
      render: (r) => <span className={r.late_minutes > 0 ? 'text-amber-700 font-medium' : 'text-neutral-400'}>{fmtMin(r.late_minutes)}</span> },
    { key: 'early_out_minutes', header: 'Early Out', align: 'right', width: 100,
      render: (r) => <span className={r.early_out_minutes > 0 ? 'text-amber-700' : 'text-neutral-400'}>{fmtMin(r.early_out_minutes)}</span> },
    { key: 'status', header: 'Status', align: 'center', width: 80,
      render: (r) => <StatusPill status={r.status} /> },
    { key: 'remark', header: 'Remark', sortable: false,
      render: (r) => <span className="text-[11px] text-neutral-500">{r.remark === '--' ? '' : r.remark}</span> },
  ];

  return (
    <div className="space-y-3">
      {!hideInternalFilters && (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs text-neutral-500 mr-1">Filter:</span>
          <ThresholdFilter label="Late" unit="m" value={lateGt} onChange={setLateGtLocal} presets={[0, 15, 30, 60]} testid="filter-late" />
          <ThresholdFilter label="OT" unit="m" value={otGt} onChange={setOtGtLocal} presets={[0, 30, 60, 120]} testid="filter-ot" />
          <ThresholdFilter label="Work" unit="m" operator="<" value={workLt} onChange={setWorkLtLocal} presets={[480, 420, 360]} testid="filter-work" />
          <StatusButtons value={statusFilter} onChange={setStatusFilterLocal} />
          <button
            onClick={refresh}
            className="ml-auto text-xs text-neutral-500 hover:text-neutral-800"
            data-testid="refresh-daily"
          >
            Refresh
          </button>
        </div>
      )}

      <DataTable
        columns={cols}
        items={items}
        isLoading={isLoading}
        isLoadingMore={isLoadingMore}
        hasMore={hasMore}
        onLoadMore={loadMore}
        error={error}
        searchable
        csvExport={{ filename: 'attendance_daily',
          columns: [
            { key: 'date', header: 'Date' },
            { key: 'name', header: 'Employee' },
            { key: 'empcode', header: 'Empcode' },
            { key: 'in_time', header: 'In' },
            { key: 'out_time', header: 'Out' },
            { key: 'work_minutes', header: 'Work (min)' },
            { key: 'overtime_minutes', header: 'OT (min)' },
            { key: 'late_minutes', header: 'Late (min)' },
            { key: 'early_out_minutes', header: 'Early Out (min)' },
            { key: 'status', header: 'Status' },
            { key: 'remark', header: 'Remark' },
          ] }}
        emptyState="No matching rows. Try a wider range or clear filters."
        estRowHeight={52}
        maxHeight={620}
        testid="attendance-daily-table"
      />
    </div>
  );
};

// ─── helpers ───────────────────────────────────────────────────────────────
const fmtMin = (m) => {
  if (!m) return '—';
  const h = Math.floor(m / 60);
  const mm = m % 60;
  return h > 0 ? `${h}h ${String(mm).padStart(2, '0')}m` : `${mm}m`;
};

const StatusPill = ({ status }) => {
  if (!status) return <span className="text-neutral-400">—</span>;
  const s = String(status).toUpperCase();
  const styles = {
    P: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    A: 'bg-red-50 text-red-700 border-red-200',
    HD: 'bg-amber-50 text-amber-700 border-amber-200',
    WO: 'bg-neutral-50 text-neutral-500 border-neutral-200',
    H: 'bg-blue-50 text-blue-700 border-blue-200',
    WEEKEND: 'bg-slate-100 text-slate-600 border-slate-200',
    HOLIDAY: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  };
  // Pretty label: "WEEKEND" → "Weekend"
  const label = s === 'WEEKEND' ? 'Weekend' : s === 'HOLIDAY' ? 'Holiday' : s;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium border ${styles[s] || styles.WO}`}>
      {label}
    </span>
  );
};

const StatusButtons = ({ value, onChange }) => {
  const opts = [
    { v: '', label: 'All' },
    { v: 'P', label: 'Present' },
    { v: 'A', label: 'Absent' },
    { v: 'HD', label: 'Half-day' },
  ];
  return (
    <div className="inline-flex items-center gap-1">
      {opts.map((o) => (
        <button
          key={o.v}
          onClick={() => onChange(o.v)}
          data-testid={`status-${o.v || 'all'}`}
          className={`px-2.5 py-1 text-[11px] rounded-full border ${value === o.v ? 'bg-pink-50 text-pink-700 border-pink-300' : 'bg-white text-neutral-600 border-neutral-200 hover:border-neutral-300'}`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
};

// ─── Date range picker ─────────────────────────────────────────────────────
const RangeBar = ({ fromDate, toDate, onChange }) => {
  const quick = (n) => {
    const to = new Date();
    const from = new Date();
    from.setDate(from.getDate() - n);
    onChange(formatDateToLocal(from), formatDateToLocal(to));
  };
  const thisMonth = () => {
    const t = new Date();
    onChange(formatDateToLocal(new Date(t.getFullYear(), t.getMonth(), 1)), formatDateToLocal(t));
  };

  return (
    <div className="flex flex-wrap items-end gap-2 mb-5">
      <div>
        <label className="block text-[11px] uppercase tracking-wider text-neutral-500 mb-1">From</label>
        <input type="date" value={fromDate} onChange={(e) => onChange(e.target.value, toDate)}
          className="px-3 py-2 border border-neutral-200 rounded-md text-sm" data-testid="range-from" />
      </div>
      <div>
        <label className="block text-[11px] uppercase tracking-wider text-neutral-500 mb-1">To</label>
        <input type="date" value={toDate} onChange={(e) => onChange(fromDate, e.target.value)}
          className="px-3 py-2 border border-neutral-200 rounded-md text-sm" data-testid="range-to" />
      </div>
      <div className="flex items-center gap-1 ml-2">
        {[
          ['7d', () => quick(7)],
          ['30d', () => quick(30)],
          ['90d', () => quick(90)],
          ['Month', thisMonth],
        ].map(([lbl, fn]) => (
          <button key={lbl} onClick={fn}
            className="px-2.5 py-1.5 text-[11px] border border-neutral-200 rounded-md hover:bg-neutral-50">
            {lbl}
          </button>
        ))}
      </div>
    </div>
  );
};

// ─── Page entry ────────────────────────────────────────────────────────────
const AdminAttendancePage = () => {
  const today = new Date();
  const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const [fromDate, setFromDate] = useState(formatDateToLocal(firstOfMonth));
  const [toDate, setToDate] = useState(getTodayLocal());
  const [drillEmployee, setDrillEmployee] = useState(null);
  const [drillFilter, setDrillFilter] = useState(null);

  const tabs = [
    {
      key: 'snapshot',
      label: 'Snapshot',
      icon: BarChart3,
      render: () => (
        <SnapshotTab
          fromDate={fromDate}
          toDate={toDate}
          onPickEmployee={(r) => {
            setDrillEmployee(r);
            const params = new URLSearchParams(window.location.search);
            params.set('tab', 'daily');
            params.set('employee_id', r.employee_id);
            window.history.replaceState(null, '', `${window.location.pathname}?${params.toString()}`);
            toast.info(`Drilling into ${r.name}'s daily timeline`);
          }}
        />
      ),
    },
    {
      key: 'daily',
      label: drillEmployee ? `Daily — ${drillEmployee.name}` : 'Daily',
      icon: CalendarDays,
      render: () => (
        <DailyTab
          fromDate={fromDate}
          toDate={toDate}
          employeeId={drillEmployee?.employee_id || null}
          presetLateGt={drillFilter === 'late' ? 0 : null}
        />
      ),
    },
    {
      key: 'heatmap',
      label: drillEmployee ? `Heatmap — ${drillEmployee.name}` : 'Heatmap',
      icon: Grid3x3,
      render: () => (
        <AttendanceHeatmap
          employeeId={drillEmployee?.employee_id || null}
          employeeName={drillEmployee?.name}
        />
      ),
    },
  ];

  return (
    <div className="p-6 max-w-[1600px] mx-auto" data-testid="admin-attendance-page">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-[#1a1a2e]">Attendance</h1>
        <p className="text-sm text-neutral-500 mt-1">
          Org-wide attendance from E-TIMEOFFICE machine data. Click any employee to drill into their day-by-day record.
        </p>
      </div>

      <RangeBar
        fromDate={fromDate}
        toDate={toDate}
        onChange={(f, t) => { setFromDate(f); setToDate(t); }}
      />

      {drillEmployee && (
        <div className="mb-3 inline-flex items-center gap-2 px-3 py-1.5 bg-pink-50 border border-pink-200 rounded-full text-xs text-pink-800">
          <span>Drilling into <strong>{drillEmployee.name}</strong></span>
          <button onClick={() => setDrillEmployee(null)}
            className="ml-1 text-pink-600 hover:text-pink-800" data-testid="clear-drill">
            ✕ Clear
          </button>
        </div>
      )}

      <DataTabs tabs={tabs} defaultTab="snapshot" urlParam="tab" testid="attendance-tabs" />
    </div>
  );
};

export default AdminAttendancePage;
